import { z } from "zod";

export function requiredString(label: string) {
  return z.preprocess(
    (value) => (typeof value === "string" ? value.trim() : value),
    z
      .string({
        required_error: `${label} is required.`,
        invalid_type_error: `${label} is required.`,
      })
      .min(1, `${label} is required.`)
  );
}

export const trimmedString = z.preprocess(
  (value) => (typeof value === "string" ? value.trim() : value),
  z
    .string({
      required_error: "Value is required.",
      invalid_type_error: "Value is required.",
    })
    .min(1, "Value is required.")
);

export const optionalTrimmedString = z.preprocess(
  (value) => {
    if (typeof value !== "string") {
      return value;
    }
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  },
  z.string().optional()
);

const isoDateString = z.preprocess(
  (value) => (typeof value === "string" ? value.trim() : value),
  z
    .string()
    .refine(
      (value) => !Number.isNaN(Date.parse(value)),
      "Value must be a valid ISO 8601 datetime."
    )
);

export const optionalIsoDateString = isoDateString.optional();

export const stringArray = z
  .array(z.string())
  .transform((values) =>
    values.map((value) => value.trim()).filter((value) => value.length > 0)
  );

export const optionalStringArray = stringArray.optional();

export const booleanFromString = z.preprocess((value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }
  return value;
}, z.boolean());

export const optionalBooleanFromString = booleanFromString.optional();

export const nonNegativeNumber = z.preprocess((value) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isNaN(parsed) ? value : parsed;
}, z.number().min(0, "Value must be a non-negative number."));

export const optionalNonNegativeNumber = nonNegativeNumber.optional();

export const optionalNullableNonNegativeNumber = z.union([
  nonNegativeNumber,
  z.null(),
  z.undefined(),
]);

export const hourOfDay = z.preprocess((value) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isNaN(parsed) ? value : parsed;
}, z.number().min(0, "Digest hour must be between 0 and 23.").max(23, "Digest hour must be between 0 and 23."));

export const optionalHourOfDay = hourOfDay.optional();
